import React from 'react';
import { useTranslation } from 'react-i18next';
import { Palette, Radio, ArrowRight } from 'lucide-react';
import PageTransition from '../components/common/PageTransition';
import SEOHead from '../components/common/SEOHead';
import LinkCard from '../components/common/LinkCard';
import ShopCard from '../components/common/ShopCard';
import SocialIcon from '../components/common/SocialIcon';
import { heroSocials, getSocialById } from '../data/socialLinks';

const Home: React.FC = () => {
  const { t } = useTranslation();

  return (
    <PageTransition>
      <SEOHead title="Home" />
      <div className="space-y-12">

        {/* Hero Section */}
        <section className="flex flex-col items-center text-center space-y-6 pt-4">
          <div className="relative group cursor-pointer">
            <div className="absolute -inset-1 bg-gradient-to-r from-brand-amber/40 via-brand-purple/40 to-brand-indigo/40 rounded-full blur-md opacity-80 group-hover:opacity-100 transition duration-500" />
            <img
              className="relative rounded-full w-32 h-32 object-cover border-2 border-white/20 shadow-2xl transition duration-300 group-hover:scale-105"
              src="/Kynothan512.png"
              alt="Kynothan"
            />
          </div>

          <div className="space-y-2">
            <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-slate-900 dark:text-white font-heading">
              Kynothan
            </h1>
            <p className="text-lg font-semibold text-transparent bg-clip-text bg-gradient-to-r from-brand-amber via-brand-purple to-brand-indigo tracking-wide uppercase">
              {t('home.title')}
            </p>
            <p className="text-sm md:text-base text-slate-600 dark:text-slate-400 max-w-md mx-auto">
              {t('home.subtitle')}
            </p>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-3">
            {heroSocials.map(id => {
              const social = getSocialById(id);
              return social ? <SocialIcon key={social.id} social={social} size="lg" /> : null;
            })}
          </div>
        </section>

        {/* Shop Card */}
        <ShopCard
          href="https://shop.kynothan.com"
          imageSrc="/img/IMG_20260327_081744.jpg"
          badge={t('home.shopCard.badge')}
          title={t('home.shopCard.title')}
          subtitle={t('home.shopCard.subtitle')}
          ctaText={t('home.shopCard.cta')}
        />

        {/* Ecosystems Cards */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <LinkCard
            href="/art"
            title={t('home.artCard.title')}
            subtitle={t('home.artCard.subtitle')}
            imageSrc="/img/IMG_20260327_081744 square.jpg"
            badge="Portfolio"
          />
          <LinkCard
            href="/stream"
            title={t('home.streamCard.title')}
            subtitle={t('home.streamCard.subtitle')}
            imageSrc="/img/twitch.svg"
            badge="Live"
          />
        </section>

        {/* Quick Contact CTA */}
        <section className="art-card p-8 rounded-3xl text-center space-y-4 bg-gradient-to-b from-brand-indigo/5 to-transparent border-brand-indigo/20">
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white font-heading">
            {t('home.collaboration.title')}
          </h2>
          <p className="text-slate-600 dark:text-slate-400">
            {t('home.collaboration.subtitle')}
          </p>
          <div className="pt-2">
            <a
              href="/contact"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-bold hover:scale-105 transition-transform"
            >
              <span>{t('home.collaboration.cta')}</span>
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </section>

      </div>
    </PageTransition>
  );
};

export default Home;
